import { useCallback, useState } from 'react';
import { useToast } from '@kinesis/bungle';
import { useSettingsSelection } from '@/hooks/use-settings-selection';
import {
  OrganisationExports,
  partKey,
} from '@/components/organisation-exports';
import {
  useListOrganisationExportsQuery,
  useDownloadOrganisationExportPartMutation,
} from '@/api/exports';

const SettingsOrganisationExports = () => {
  const toast = useToast('globalTop');
  const { id } = useSettingsSelection() as any;
  const { data: exports, isLoading } = useListOrganisationExportsQuery(id);
  const [download] = useDownloadOrganisationExportPartMutation();
  // Parts whose links are being generated, as `<export id>:<n>`; each
  // settles on its own.
  const [downloading, setDownloading] = useState<string[]>([]);
  const [unavailable, setUnavailable] = useState<string[]>([]);

  // The link is minted on click and expires shortly after; the mutation
  // follows it straight away rather than it being rendered.
  const onDownload = useCallback(
    async (exportId: string, n: number) => {
      const key = partKey(exportId, n);
      setDownloading((keys) => [...keys, key]);
      try {
        await download({
          organisationId: id,
          exportId,
          n,
        }).unwrap();
      } catch (e: any) {
        if (e?.status === 404) {
          setUnavailable((ids) => [...ids, exportId]);
        } else {
          toast(`Failed to download part ${n}.`, { variant: 'error' });
        }
      } finally {
        setDownloading((keys) => keys.filter((k) => k !== key));
      }
    },
    [download, id, toast],
  );

  return (
    <OrganisationExports
      key={id}
      exports={exports}
      isLoading={isLoading}
      downloading={downloading}
      unavailable={unavailable}
      onDownload={onDownload}
    />
  );
};

export { SettingsOrganisationExports };
